import React from 'react';
import type { BrandingValues, BrandingSurface } from './brandingTypes';

interface Props {
  surface: BrandingSurface;
  value: BrandingValues;
  organizationName: string;
}

const SURFACE_LABEL: Record<BrandingSurface, string> = {
  outreach: 'Public outreach page',
  report: 'Report',
};

/**
 * A sample of what the branding does to one surface.
 *
 * The report sample is deliberately not a survey card: an admin branding their
 * reports should see a report. It is a small lie only in the sense that report
 * output does not honor these values yet (the settings ship first, the render
 * path is separate NGR work) — which is why the panel says so rather than
 * letting a convincing preview imply otherwise.
 */
const BrandingPreview: React.FC<Props> = ({ surface, value, organizationName }) => {
  const logoAlign =
    value.logo_position === 'center'
      ? 'text-center'
      : value.logo_position === 'right'
        ? 'text-right'
        : 'text-left';

  return (
    <div className="border border-border-light dark:border-border-dark rounded-lg overflow-hidden">
      <div className="bg-background-tertiary dark:bg-background-dark-tertiary px-4 py-2 border-b border-border-light dark:border-border-dark">
        <span className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
          {SURFACE_LABEL[surface]} preview
        </span>
      </div>

      <div
        className="p-6 min-h-[320px]"
        style={{ backgroundColor: value.background_color, fontFamily: value.body_font }}
      >
        {value.show_organization_logo && (
          <div className={`mb-6 ${logoAlign}`}>
            <div
              className="inline-block px-4 py-2 rounded"
              style={{ backgroundColor: value.primary_color, color: '#fff' }}
            >
              {organizationName} Logo
            </div>
          </div>
        )}

        {value.custom_header_html && (
          <div className="mb-4" dangerouslySetInnerHTML={{ __html: value.custom_header_html }} />
        )}

        <div
          className="rounded-lg p-6"
          style={{
            backgroundColor: value.layout_template === 'minimal' ? 'transparent' : '#ffffff',
            boxShadow:
              value.layout_template === 'minimal' ? 'none' : '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          {surface === 'outreach' ? (
            <>
              <h2
                className="text-xl font-bold mb-3"
                style={{ fontFamily: value.heading_font, color: value.text_color }}
              >
                Sample Survey Title
              </h2>
              <p className="mb-4" style={{ color: value.secondary_color }}>
                This is a preview of how your outreach pages will look.
              </p>
              <button
                type="button"
                className="px-4 py-2 rounded-lg font-medium"
                style={{ backgroundColor: value.primary_color, color: '#ffffff' }}
              >
                Submit Response
              </button>
            </>
          ) : (
            <>
              <h2
                className="text-xl font-bold mb-1"
                style={{ fontFamily: value.heading_font, color: value.text_color }}
              >
                Quarterly Bill Activity
              </h2>
              <p className="text-sm mb-4" style={{ color: value.secondary_color }}>
                {organizationName} · sample report
              </p>

              <div className="grid grid-cols-2 gap-3 mb-4">
                {[
                  { label: 'Bills tracked', figure: '128' },
                  { label: 'Positions taken', figure: '41' },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-lg p-3"
                    style={{ backgroundColor: `${value.primary_color}14` }}
                  >
                    <div
                      className="text-2xl font-bold"
                      style={{ fontFamily: value.heading_font, color: value.primary_color }}
                    >
                      {stat.figure}
                    </div>
                    <div className="text-xs" style={{ color: value.secondary_color }}>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              <div className="rounded-lg overflow-hidden border" style={{ borderColor: '#e5e7eb' }}>
                <div
                  className="grid grid-cols-3 px-3 py-2 text-xs font-semibold"
                  style={{ backgroundColor: value.primary_color, color: '#ffffff' }}
                >
                  <span>Bill</span>
                  <span>Status</span>
                  <span>Position</span>
                </div>
                {[
                  ['HB25-1001', 'In committee', 'Support'],
                  ['SB25-014', 'Passed House', 'Monitor'],
                ].map((row) => (
                  <div
                    key={row[0]}
                    className="grid grid-cols-3 px-3 py-2 text-xs"
                    style={{ color: value.text_color }}
                  >
                    {row.map((cell) => (
                      <span key={cell}>{cell}</span>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {value.custom_footer_html && (
          <div className="mt-6" dangerouslySetInnerHTML={{ __html: value.custom_footer_html }} />
        )}
      </div>
    </div>
  );
};

export default BrandingPreview;
